import type { ThemeClasses, ViewType } from "../types";
import type { TFunction } from "i18next";
import Select from "react-select";
import { getCustomSelectStyles, FilterPopover } from "../../../../shared";
import { useAllDevices } from "../../../../../../features/devices/queries";
import { useStatuses } from "../../../../../../features/catalogs/queries";
import { useMemo } from "react";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";

interface TripFiltersProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  selectedStatus: string;
  searchQuery: string;
  selectedDeviceId: string;
  dateFrom: string;
  dateTo: string;
  onStatusChange: (status: string) => void;
  onSearchChange: (search: string) => void;
  onDeviceChange: (deviceId: string) => void;
  onDateFromChange: (date: string) => void;
  onDateToChange: (date: string) => void;
  onClearFilters: () => void;
  onViewChange: (view: ViewType) => void;
  selectedView: ViewType;
  t: TFunction;
}

export function TripFilters({
  themeClasses,
  isDarkMode,
  selectedStatus,
  searchQuery,
  selectedDeviceId,
  dateFrom,
  dateTo,
  onStatusChange,
  onSearchChange,
  onDeviceChange,
  onDateFromChange,
  onDateToChange,
  onViewChange,
  selectedView,
  t,
}: TripFiltersProps) {
  const { data: devices = [] } = useAllDevices();
  const { data: statuses = [] } = useStatuses();

  // Get status options for trips (ACTIVE, INACTIVE)
  const statusOptions = useMemo(
    () => getStatusOptionsForEntity(statuses, "trips", t),
    [statuses, t]
  );

  const deviceOptions = useMemo(
    () => devices.map((d) => {
      const vehicleName = d.vehicle ? d.vehicle?.name : "";
      const trailerName = d.remolque ? d.remolque?.name : "";
      const nameDevice = vehicleName || trailerName ? d.identifier : d.name;
      return {
        value: d.id,
        label: `${nameDevice} ${vehicleName ? `(${vehicleName})` : ""} ${trailerName ? `(${trailerName})` : ""}`.trim(),
      };
    }),
    [devices]
  );

  // El buscador queda fuera del popover, así que no cuenta como filtro activo.
  const activeCount = [selectedStatus, selectedDeviceId, dateFrom, dateTo].filter(Boolean).length;

  const fieldBaseClass = `w-full py-2 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${
    themeClasses.text
  } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm`;

  const label = `block text-[11px] mb-1 ${themeClasses.textSecondary}`;

  return (
    <div
      className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 sm:px-6 pb-3`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {/* Search */}
        <div className="relative flex-1 min-w-0 sm:flex-none sm:w-72">
          <i
            className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
          ></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("trips.searchPlaceholder")}
            aria-label={t("trips.searchPlaceholder")}
            className={`${fieldBaseClass} h-9 pl-9`}
          />
        </div>

        <FilterPopover
          isDarkMode={isDarkMode}
          activeCount={activeCount}
          onClear={() => {
            onStatusChange("");
            onDeviceChange("");
            onDateFromChange("");
            onDateToChange("");
          }}
        >
          {/* Status filter */}
          <div>
            <label className={label}>{t("trips.filterStatus")}</label>
            <Select
              options={statusOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("trips.filterStatus")}
              value={statusOptions.find((opt) => opt.value === selectedStatus) || null}
              onChange={(option: any) => onStatusChange(option?.value || "")}
              isSearchable
              isClearable
              noOptionsMessage={() => t("trips.filters.noStatusesAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Device filter */}
          <div>
            <label className={label}>{t("trips.filters.device")}</label>
            <Select
              options={deviceOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("trips.filters.devicePlaceholder")}
              value={deviceOptions.find((opt) => opt.value === selectedDeviceId) || null}
              onChange={(option: any) => onDeviceChange(option?.value || "")}
              isSearchable
              isClearable
              noOptionsMessage={() => t("trips.filters.noDevicesAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-2">
            <div className="min-w-0">
              <label className={label}>{t("trips.filters.dateFrom")}</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                className={`${fieldBaseClass} px-2`}
                title={t("trips.filters.dateFrom")}
              />
            </div>
            <div className="min-w-0">
              <label className={label}>{t("trips.filters.dateTo")}</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                min={dateFrom || undefined}
                className={`${fieldBaseClass} px-2`}
                title={t("trips.filters.dateTo")}
              />
            </div>
          </div>
        </FilterPopover>

        {/* View toggle */}
        <div className="ml-auto flex shrink-0">
          <div
            className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
          >
            <button
              className={`px-3 py-1 rounded-md transition-colors ${
                selectedView === "grid"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
              }`}
              onClick={() => onViewChange("grid")}
            >
              <i className="ri-grid-fill"></i>
            </button>
            <button
              className={`px-3 py-1 rounded-md transition-colors ${
                selectedView === "list"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
              }`}
              onClick={() => onViewChange("list")}
            >
              <i className="ri-list-check"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
